import React, { useState } from 'react';
import { View, Text, TouchableOpacity, Image, Platform, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import PostGridDisplay, { COL_WIDTH, ROW_HEIGHT } from './PostGridDisplay';
import { GRID_COLS, GRID_ROWS } from '../utils/postGridEngine';
import { getResponsiveValue } from '../styles/breakpoints';
import { CardInterestsDisplay } from './CardInterestsDisplay';

const GRID_W = GRID_COLS * COL_WIDTH;
const GRID_H = GRID_ROWS * ROW_HEIGHT;

const CARD_PADDING = getResponsiveValue({ mobile: 12, tablet: 16, laptop: 20, desktop: 20 });
const HEADER_FONT_SIZE = getResponsiveValue({ mobile: 20, tablet: 24, laptop: 28, desktop: 28 });
const LOCATION_FONT_SIZE = getResponsiveValue({ mobile: 15, tablet: 17, laptop: 20, desktop: 20 });

export function CommunityPostCard({
  post,
  onPress,
  onFolderPress,
  onMessagePress,
  onImagePress,
  rightActions = null,
  showFolderButton = true,
  showMessageButton = true,
  /** When false and there is no headline/location, the header stays blank (no "Post" fallback). */
  showHeadlinePlaceholder = true,
  activeOpacity = 0.95,
  showAdminBadge = false,
  interestEmojis = [],
  interestItems = [],
  showInterestEmojis = false,
  compact = false,
  compactBadge = null,
}) {
  const headerColor = post?.headerTextColor || undefined;
  const postItems = Array.isArray(post?.items) ? post.items : [];
  const [contentWidth, setContentWidth] = useState(0);

  const scale = contentWidth > 0 ? Math.min(1, contentWidth / GRID_W) : 1;
  const compactCellSize = contentWidth > 0 ? contentWidth / GRID_COLS : COL_WIDTH;
  const displayW = compact ? compactCellSize * GRID_COLS : GRID_W * scale;
  const displayH = compact ? compactCellSize * GRID_ROWS : GRID_H * scale;

  // Top-left origin scaling: for web use CSS transformOrigin, for native use translate+scale
  const getScaleStyle = () => {
    if (scale >= 1) return {};
    if (Platform.OS === 'web') {
      return { transformOrigin: 'top left', transform: `scale(${scale})` };
    }
    return {
      transform: [
        { translateX: -(GRID_W * (1 - scale) / 2) },
        { translateY: -(GRID_H * (1 - scale) / 2) },
        { scale },
      ],
    };
  };

  return (
    <TouchableOpacity
      style={[styles.postCard, compact && styles.postCardCompact]}
      onPress={onPress}
      activeOpacity={activeOpacity}
    >
      {/* Admin-only disclaimer banner */}
      {showAdminBadge && (
        <View style={styles.adminOnlyBanner}>
          <Ionicons name="eye-off-outline" size={12} color="#fff" />
          <Text style={styles.adminOnlyBannerText}>Admin only — hidden from public</Text>
        </View>
      )}
      {post?.cardBackground?.type === 'color' && post.cardBackground.value ? (
        <View style={[styles.postCardBgLayer, { backgroundColor: post.cardBackground.value }]} />
      ) : null}
      {post?.cardBackground?.type === 'image' && post.cardBackground.value ? (
        <Image source={{ uri: post.cardBackground.value }} style={styles.postCardBgLayer} resizeMode="cover" />
      ) : null}
      {post?.cardBackground?.type === 'video' && post.cardBackground.value && Platform.OS === 'web' ? (
        <video
          src={post.cardBackground.value}
          autoPlay
          loop
          muted
          playsInline
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12, zIndex: 0 }}
        />
      ) : null}

      <View style={styles.postCardHeader}>
        <View style={styles.postCardHeaderInputs}>
          {post?.postContent ? (
            <Text style={[
              styles.postCardPostInput,
              headerColor ? { color: headerColor } : undefined,
            ]} numberOfLines={2}>
              {post.postContent}
            </Text>
          ) : null}
          {post?.location ? (
            <Text style={[
              styles.postCardLocationInput,
              headerColor ? { color: headerColor } : undefined,
            ]} numberOfLines={1}>
              {post.location}
            </Text>
          ) : null}
          {showHeadlinePlaceholder && !post?.postContent && !post?.location ? (
            <Text style={[
              styles.postCardPostInput,
              headerColor ? { color: headerColor } : undefined,
            ]} numberOfLines={1}>
              Post
            </Text>
          ) : null}
        </View>
        <View style={styles.postCardActionsCol}>
          <View style={styles.postCardActionsRow}>
            {compactBadge?.label ? (
              <View style={styles.compactBadgeCol}>
                <Text style={[
                  styles.compactBadgeType,
                  compactBadge.color ? { color: compactBadge.color } : null,
                ]}>
                  {compactBadge.label}
                </Text>
                {compactBadge.price ? (
                  <Text style={[
                    styles.compactBadgePrice,
                    compactBadge.color ? { color: compactBadge.color } : null,
                  ]}>
                    ${compactBadge.price}
                  </Text>
                ) : null}
              </View>
            ) : null}
            {rightActions}
            {showMessageButton ? (
              <TouchableOpacity
                style={styles.postCardMessageBtn}
                onPress={(e) => {
                  if (e?.stopPropagation) e.stopPropagation();
                  if (onMessagePress) onMessagePress(post);
                }}
              >
                <Image
                  source={require('../../assets/icons/paper-plane.png')}
                  style={styles.postCardMessageIcon}
                />
              </TouchableOpacity>
            ) : null}
            {showFolderButton ? (
              <TouchableOpacity
                style={styles.postCardFolderBtn}
                onPress={(e) => {
                  if (e?.stopPropagation) e.stopPropagation();
                  if (onFolderPress) onFolderPress(post);
                }}
              >
                <Ionicons name="folder-open" size={28} color="#bca785" strokeWidth={1} stroke="#bca785" />
              </TouchableOpacity>
            ) : null}
          </View>
          {showInterestEmojis ? (
            <View style={styles.postCardInterestsRow}>
              <CardInterestsDisplay items={interestItems} emojis={interestEmojis} />
            </View>
          ) : null}
        </View>
      </View>

      <View
        style={styles.postCardGridWrapper}
        onLayout={(e) => setContentWidth(e.nativeEvent.layout.width)}
      >
        {contentWidth > 0 && (
          <View style={{ width: displayW, height: displayH, overflow: 'hidden' }}>
            <View style={compact
              ? { width: displayW, height: displayH }
              : [{ width: GRID_W, height: GRID_H }, getScaleStyle()]
            }>
              <PostGridDisplay
                items={postItems}
                cardBackground={null}
                onImagePress={onImagePress}
                {...(compact ? {
                  colWidth: compactCellSize,
                  rowHeight: compactCellSize,
                  suppressEmptyWidgetHints: true,
                  fontScale: 0.8,
                } : null)}
              />
            </View>
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  postCard: {
    backgroundColor: '#feffe4',
    borderRadius: 12,
    overflow: 'hidden',
    padding: CARD_PADDING,
    alignSelf: 'stretch',
    width: '100%',
    position: 'relative',
  },
  postCardCompact: {
    padding: 12,
    borderRadius: 8,
  },
  compactBadgeCol: {
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
    marginRight: 4,
  },
  compactBadgeType: {
    fontFamily: 'Computer Says No',
    fontSize: Math.round(LOCATION_FONT_SIZE * 0.8),
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  compactBadgePrice: {
    fontFamily: 'Computer Says No',
    fontSize: HEADER_FONT_SIZE,
    fontWeight: '700',
    lineHeight: HEADER_FONT_SIZE + 4,
  },
  postCardBgLayer: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: 12,
    overflow: 'hidden',
    zIndex: 0,
  },
  adminOnlyBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: '#e74c3c',
    borderRadius: 6,
    paddingVertical: 4,
    paddingHorizontal: 10,
    marginBottom: 10,
    alignSelf: 'flex-start',
    zIndex: 10,
  },
  adminOnlyBannerText: {
    color: '#fff',
    fontSize: 11,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  postCardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 12,
    zIndex: 10,
    width: '100%',
  },
  postCardHeaderInputs: {
    flex: 1,
  },
  postCardActionsCol: {
    alignItems: 'flex-end',
    gap: 4,
    flexShrink: 0,
  },
  postCardActionsRow: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: 5,
    alignItems: 'center',
    justifyContent: 'flex-end',
    position: 'relative',
    zIndex: 30,
  },
  postCardInterestsRow: {
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    zIndex: 1,
  },
  postCardPostInput: {
    fontFamily: 'Computer Says No',
    fontSize: HEADER_FONT_SIZE,
    color: '#000',
    paddingVertical: 2,
  },
  postCardLocationInput: {
    fontFamily: 'Computer Says No',
    fontSize: LOCATION_FONT_SIZE,
    color: '#333',
    paddingVertical: 2,
  },
  postCardFolderBtn: {
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
  },
  postCardMessageBtn: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    borderRadius: 8,
  },
  postCardMessageIcon: {
    width: 20,
    height: 20,
  },
  postCardGridWrapper: {
    width: '100%',
    alignItems: 'center',
  },
});
